import * as React from 'react';
import { UIHeading1, Body2 } from '@cian/ui-kit';

import { createMockNewbuildingsCompareData } from './mocks';
import { useFloatingHeader } from './hooks/useFloatingHeader';
import { HeaderRow } from './parts/HeaderRow';
import * as style from './style.css';

const COLUMN_WIDTH = 320;

export const NewbuildingsComparePage = () => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const floatingHeaderScrollRef = React.useRef<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement | null>(null);
  const dragStartRef = React.useRef({ scrollLeft: 0, x: 0 });
  const [dragging, setDragging] = React.useState(false);
  const { headers, rows } = createMockNewbuildingsCompareData();
  const { isFloatingHeaderMounted, isFloatingHeaderVisible, syncFloatingHeaderScroll } = useFloatingHeader({
    floatingHeaderScrollRef,
    headerRowRef,
    scrollRef,
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    const scrollElement = scrollRef.current;
    scrollElement?.scrollBy({
      behavior: 'smooth',
      left: event.key === 'ArrowLeft' ? -COLUMN_WIDTH : COLUMN_WIDTH,
    });

    if (scrollElement) {
      syncFloatingHeaderScroll(scrollElement.scrollLeft);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    syncFloatingHeaderScroll(event.currentTarget.scrollLeft);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !scrollRef.current) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      scrollLeft: scrollRef.current.scrollLeft,
      x: event.clientX,
    };
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !scrollRef.current) {
      return;
    }

    const distance = event.clientX - dragStartRef.current.x;
    scrollRef.current.scrollLeft = dragStartRef.current.scrollLeft - distance;
    syncFloatingHeaderScroll(scrollRef.current.scrollLeft);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setDragging(false);
  };

  return (
    <main className={style['container']} data-testid="newbuildings-compare_page">
      <div className={style['title']}>
        <UIHeading1 as="h1">Сравнение</UIHeading1>
      </div>
      {isFloatingHeaderMounted && (
        <div
          className={isFloatingHeaderVisible ? style['floating-header-visible'] : style['floating-header']}
          data-testid="FloatingHeader"
        >
          <div ref={floatingHeaderScrollRef} className={style['floating-header-scroller']}>
            <div className={style['table']} role="table">
              <HeaderRow floating headers={headers} />
            </div>
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        className={dragging ? style['table-scroller-dragging'] : style['table-scroller']}
        data-testid="newbuildings-compare_table"
        onKeyDown={handleKeyDown}
        onPointerCancel={handlePointerEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onScroll={handleScroll}
        role="region"
        tabIndex={0}
      >
        <div className={style['table']} role="table">
          <HeaderRow headers={headers} rowRef={headerRowRef} />
          {rows.map(row => (
            <div className={style['row']} key={row.id} role="row">
              <div className={style['cell']} role="cell">
                <Body2>{row.criterion}</Body2>
              </div>
              {row.cells.map(cell => (
                <div className={style['cell']} key={cell.id} role="cell">
                  <Body2>{cell.value}</Body2>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};
